/**
 * <CatalogBrowsePage> — shared list + filters + detail sheet (AUTO-8).
 *
 * Parameterised by `kind`. Reused by all 6 per-kind Discover routes so
 * the UI doesn't drift across tools/skills/prompts/functions/agents/
 * connections (per feedback_shared_component_scope_prop in memory: one
 * component, parameterised, NOT six near-copies).
 *
 * Render flow:
 *   - Top: search bar (text + "semantic" toggle when supported)
 *   - Side: filter chips (category, system/tenant source)
 *   - Center: result cards (paginated client-side; backend caps at 200)
 *   - Right (on click): item-detail sheet with extras + (optional) preview
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Sparkles,
  Filter,
  Globe,
  User as UserIcon,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { PromptPreviewPanel } from './prompt-preview-panel';
import { DetailExtras } from './detail-extras';

export type DiscoverKind =
  | 'tool'
  | 'skill'
  | 'prompt'
  | 'function'
  | 'agent'
  | 'connection';

export interface DiscoverItem {
  slug: string;
  displayName: string;
  description: string;
  category: string;
  version: number;
  isSystem: boolean;
  score?: number;
}

export interface DiscoverDetail extends DiscoverItem {
  extras: Record<string, unknown>;
}

interface ListResponse {
  items: DiscoverItem[];
  total: number;
  truncated: boolean;
  searchMode: 'none' | 'text' | 'semantic' | 'semantic_fallback';
}

const SEMANTIC_SUPPORTED: ReadonlySet<DiscoverKind> = new Set(['tool', 'skill']);

interface Props {
  kind: DiscoverKind;
  title: string;
  description: string;
  icon: typeof Search;
  /** Whether to render the prompt preview panel on the detail sheet. */
  showPreview?: boolean;
  /**
   * Slug-to-canonical-clone-endpoint hint shown on the detail sheet when
   * the item is system + the kind supports cloning. The Discover surface
   * deliberately delegates the actual fork to /agents/clone (per audit
   * trail integrity); this just makes the curl/fetch instructions visible.
   */
  cloneEndpoint?: string;
}

export function CatalogBrowsePage({
  kind,
  title,
  description,
  icon: Icon,
  showPreview = false,
  cloneEndpoint,
}: Props) {
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<'text' | 'semantic'>('text');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'system' | 'tenant'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (q.trim() && mode === 'semantic' && SEMANTIC_SUPPORTED.has(kind)) {
      p.set('mode', 'semantic');
    }
    if (sourceFilter !== 'all') p.set('source', sourceFilter);
    if (categoryFilter) p.set('category', categoryFilter);
    p.set('limit', '100');
    return p;
  }, [q, mode, sourceFilter, categoryFilter, kind]);

  const listQuery = useQuery({
    queryKey: ['admin', 'discover', kind, queryParams.toString()],
    queryFn: () =>
      apiClient.get<ListResponse>(
        `/admin/discover/${kind}?${queryParams.toString()}`,
      ),
  });

  const detailQuery = useQuery({
    queryKey: ['admin', 'discover', kind, 'detail', openSlug],
    enabled: !!openSlug,
    queryFn: () =>
      apiClient.get<DiscoverDetail>(
        `/admin/discover/${kind}/${encodeURIComponent(openSlug!)}`,
      ),
  });

  const allCategories = useMemo(() => {
    if (!listQuery.data?.items) return [];
    const set = new Set<string>();
    for (const it of listQuery.data.items) set.add(it.category);
    return Array.from(set).sort();
  }, [listQuery.data]);

  const handleClearFilters = () => {
    setQ('');
    setSourceFilter('all');
    setCategoryFilter(null);
    setMode('text');
  };

  const hasFilters =
    !!q.trim() || sourceFilter !== 'all' || !!categoryFilter || mode === 'semantic';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </h2>
        <p className="text-xs text-muted-foreground max-w-2xl">{description}</p>
      </div>

      {/* Search + mode toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={`Search ${kind}s by name, description, slug...`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        {SEMANTIC_SUPPORTED.has(kind) && (
          <div
            className="flex items-center gap-1.5 border rounded px-2 py-1 text-[11px]"
            title="Semantic search uses vector embeddings (Qdrant) to find similar entities even when keywords don't match exactly."
          >
            <Sparkles className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Mode:</span>
            <button
              type="button"
              onClick={() => setMode('text')}
              className={cn(
                'px-1.5 py-0.5 rounded',
                mode === 'text'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Text
            </button>
            <button
              type="button"
              onClick={() => setMode('semantic')}
              className={cn(
                'px-1.5 py-0.5 rounded',
                mode === 'semantic'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Semantic
            </button>
          </div>
        )}
        {listQuery.data && (
          <span className="text-xs text-muted-foreground tabular-nums ml-auto">
            {listQuery.data.items.length}{' '}
            {listQuery.data.items.length === 1 ? kind : `${kind}s`}
            {listQuery.data.truncated && '+'}
            {listQuery.data.searchMode === 'semantic_fallback' && (
              <span
                className="ml-2 text-amber-700 dark:text-amber-400"
                title="Semantic search returned no hits; fell back to text scan."
              >
                (text fallback)
              </span>
            )}
          </span>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className="text-muted-foreground flex items-center gap-1">
          <Filter className="h-3 w-3" />
          Source:
        </span>
        {(['all', 'system', 'tenant'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSourceFilter(s)}
            className={cn(
              'px-2 py-0.5 rounded border capitalize',
              sourceFilter === s
                ? 'bg-foreground text-background border-foreground'
                : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
            )}
          >
            {s}
          </button>
        ))}
        {allCategories.length > 0 && (
          <>
            <Separator orientation="vertical" className="h-4 mx-1" />
            <span className="text-muted-foreground">Category:</span>
            <button
              type="button"
              onClick={() => setCategoryFilter(null)}
              className={cn(
                'px-2 py-0.5 rounded border',
                !categoryFilter
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
              )}
            >
              All
            </button>
            {allCategories.slice(0, 12).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategoryFilter(c === categoryFilter ? null : c)}
                className={cn(
                  'px-2 py-0.5 rounded border',
                  categoryFilter === c
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
                )}
              >
                {c}
              </button>
            ))}
          </>
        )}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[11px] ml-auto"
            onClick={handleClearFilters}
          >
            Clear all
          </Button>
        )}
      </div>

      {/* Results */}
      {listQuery.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading {kind}s...
          </CardContent>
        </Card>
      ) : listQuery.error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Failed to load: {listQuery.error instanceof Error ? listQuery.error.message : String(listQuery.error)}
            </div>
          </CardContent>
        </Card>
      ) : !listQuery.data || listQuery.data.items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground space-y-2">
            <div>No {kind}s match your filters.</div>
            {hasFilters && (
              <Button size="sm" variant="outline" onClick={handleClearFilters}>
                Clear all filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {listQuery.data.items.map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => setOpenSlug(item.slug)}
              className="text-left group"
            >
              <Card className="h-full transition-all group-hover:border-foreground/30 group-hover:shadow-sm">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight truncate">
                        {item.displayName}
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground truncate">
                        {item.slug}
                      </div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground shrink-0" />
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-3">
                    {item.description}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                    <Badge variant="outline" className="font-normal gap-1">
                      {item.isSystem ? (
                        <>
                          <Globe className="h-2.5 w-2.5" /> System
                        </>
                      ) : (
                        <>
                          <UserIcon className="h-2.5 w-2.5" /> Tenant
                        </>
                      )}
                    </Badge>
                    <Badge variant="outline" className="font-normal">
                      {item.category}
                    </Badge>
                    {item.score !== undefined && (
                      <Badge
                        variant="outline"
                        className="font-mono font-normal"
                        title="Cosine similarity score (semantic search)"
                      >
                        {item.score.toFixed(2)}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}

      {/* Detail sheet */}
      <Sheet open={!!openSlug} onOpenChange={(v) => !v && setOpenSlug(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {detailQuery.data?.displayName ?? openSlug ?? 'Loading...'}
            </SheetTitle>
            <SheetDescription className="font-mono text-[11px]">
              {detailQuery.data?.slug ?? openSlug}
            </SheetDescription>
          </SheetHeader>
          {detailQuery.isLoading ? (
            <div className="py-6 text-sm text-muted-foreground animate-pulse">
              Loading...
            </div>
          ) : detailQuery.error ? (
            <div className="py-6 text-sm text-destructive">
              Failed: {detailQuery.error instanceof Error ? detailQuery.error.message : String(detailQuery.error)}
            </div>
          ) : detailQuery.data ? (
            <div className="py-4 space-y-4">
              <div className="text-sm text-foreground/90 whitespace-pre-wrap">
                {detailQuery.data.description}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                <Badge variant="outline" className="gap-1">
                  {detailQuery.data.isSystem ? (
                    <>
                      <Globe className="h-3 w-3" /> System
                    </>
                  ) : (
                    <>
                      <UserIcon className="h-3 w-3" /> Tenant
                    </>
                  )}
                </Badge>
                <Badge variant="outline">{detailQuery.data.category}</Badge>
                <Badge variant="outline">v{detailQuery.data.version}</Badge>
              </div>

              <Separator />

              <DetailExtras kind={kind} detail={detailQuery.data} />

              {showPreview && kind === 'prompt' && (
                <>
                  <Separator />
                  <PromptPreviewPanel
                    slug={detailQuery.data.slug}
                    declaredVariables={
                      (detailQuery.data.extras.variables ?? []) as Array<{
                        name: string;
                        label?: string;
                        required?: boolean;
                        default?: string;
                        description?: string;
                      }>
                    }
                  />
                </>
              )}

              {cloneEndpoint && detailQuery.data.isSystem && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Fork to your tenant</h4>
                    <p className="text-xs text-muted-foreground">
                      Discover delegates forking to the canonical clone endpoint
                      so the audit trail stays consistent:
                    </p>
                    <pre className="text-[11px] bg-muted p-2 rounded overflow-x-auto">
                      {`POST ${cloneEndpoint}\n{ "${kind}Slug": "${detailQuery.data.slug}" }`}
                    </pre>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
